import { useEffect, useState } from 'react'
import { Container, Row, Col, Card, Form, Button, Badge, Alert } from 'react-bootstrap'
import { useTranslation } from "react-i18next"
import { useParams, Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { getTask, gradeTask } from '../../../actions/taskActions'
import Loader from '../../../components/loaders/Loader'

export default function TaskDetail() {
    const { t } = useTranslation()
    const { taskId } = useParams()
    const dispatch = useDispatch()
    const { currentTask, loading, taskError } = useSelector(state => state.tasks)
    const [grade, setGrade] = useState('')
    const [feedback, setFeedback] = useState('')

    useEffect(() => {
        if (taskId) dispatch(getTask(taskId))
    }, [dispatch, taskId])

    useEffect(() => {
        if (currentTask && String(currentTask._id) === String(taskId)) {
            setGrade(currentTask.grade ?? '')
            setFeedback(currentTask.comments || '')
        }
    }, [currentTask, taskId])

    if (loading && !currentTask) return <Loader />

    if (taskError) return (
        <Container className='my-3 p-5'>
            <Alert variant='danger'>{taskError}</Alert>
        </Container>
    )

    const task = currentTask && String(currentTask._id) === String(taskId) ? currentTask : null

    const taskStatus = () => {
        if (!task) return null
        if (task.grade !== null && task.grade !== undefined) return 'graded'
        if (task.dueDate && new Date(task.dueDate) < new Date()) return 'overdue'
        return 'pending'
    }

    const status = taskStatus()
    const statusVariant = status === 'graded' ? 'success' : status === 'overdue' ? 'danger' : 'warning'
    const statusLabel = status === 'graded' ? t('teacher.tasks.graded') : status === 'overdue' ? t('teacher.tasks.overdue') : t('teacher.tasks.pending')

    const handleSaveGrade = async () => {
        if (grade === '' || grade === null) return
        const ok = await dispatch(gradeTask(taskId, { grade: Number(grade), comments: feedback }))
        if (ok) dispatch(getTask(taskId))
    }

    return (
        <Container className='my-3 p-5'>
            <Row>
                <Col>
                    <Link to='/teacher/tasks' className='text-decoration-none mb-2 d-inline-block'>{t('global.return')}</Link>
                    <div className='d-flex align-items-center gap-3 mb-1'>
                        <h3 className='fw-bold mb-0'>{task?.title || t('teacher.tasks.task')}</h3>
                        {status && <Badge bg={statusVariant} className='rounded-pill px-3 py-2'>{statusLabel}</Badge>}
                    </div>
                    <p className='text-muted mb-4'>
                        {task?.groupName || ''}
                        {task?.studentName ? ` · ${task.studentName}` : ''}
                        {task?.dueDate ? ` · ${t('teacher.tasks.duedate')}: ${new Date(task.dueDate).toLocaleDateString()}` : ''}
                    </p>
                </Col>
            </Row>
            <Row className='g-3'>
                <Col xs={12} md={7}>
                    <Card className='border-0 shadow-sm rounded-4 h-100'>
                        <Card.Body className='p-4'>
                            <h5 className='fw-bold mb-3'>{t('teacher.tasks.task')}</h5>
                            <p className='mb-0' style={{ whiteSpace: 'pre-line' }}>{task?.description || t('teacher.classes.nosessiondescription')}</p>
                            {task?.gradedAt && (
                                <p className='text-muted mt-3 mb-0' style={{ fontSize: '12px' }}>
                                    {t('teacher.tasks.gradedOn')}: {new Date(task.gradedAt).toLocaleDateString()}
                                </p>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
                <Col xs={12} md={5}>
                    <Card className='border-0 shadow-sm rounded-4 h-100'>
                        <Card.Body className='p-4'>
                            <h5 className='fw-bold mb-3'>{t('teacher.tasks.correct')}</h5>
                            <Form.Group className='mb-3'>
                                <Form.Label>{t('teacher.tasks.grade')} (0-10)</Form.Label>
                                <Form.Control
                                    type='number' min='0' max='10' step='0.5'
                                    style={{ borderRadius: '10px' }}
                                    value={grade}
                                    onChange={e => setGrade(e.target.value)}
                                />
                            </Form.Group>
                            <Form.Group className='mb-3'>
                                <Form.Label>{t('teacher.tasks.feedback')}</Form.Label>
                                <Form.Control
                                    as='textarea' rows={4}
                                    style={{ borderRadius: '10px' }}
                                    value={feedback}
                                    onChange={e => setFeedback(e.target.value)}
                                />
                            </Form.Group>
                            <Button variant='primary' className='rounded-3' onClick={handleSaveGrade} disabled={grade === ''}>
                                {t('teacher.tasks.savegrade')}
                            </Button>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    )
}
